import { Head, router } from "@inertiajs/react";
import { Layout } from "@/components/Layout";

type Contact = { name: string | null; bookable: boolean; self: boolean };

type Plan = { id: number; name: string; describe: string };

type Connection = { state: "ok" | "expired" | "never"; expires?: string; since?: string };

type Member = {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  club: string | null;
  plan: string | null;
  planId: number | null;
  planDetail: string | null;
  operator: boolean;
  status: string;
  connection: Connection;
  contacts: Contact[];
  activeRequests: number;
  upcomingCourts: number;
};

type Court = {
  court: string | null;
  startTime: string;
  duration: number;
  status: string;
  under: string | null;
};

type Request = {
  id: string;
  member: string;
  club: string;
  sport: string;
  date: string;
  startTime: string;
  backupTimes: string[];
  duration: number;
  status: string;
  mode: string;
  attempts: number;
  lastError: string | null;
  repeating: boolean;
  courts: Court[];
  attempts_seen: { at: string; outcome: string; saw: string }[];
};

export default function Overview(
  { members, upcoming, recent, plans }:
  { members: Member[]; upcoming: Request[]; recent: Request[]; plans: Plan[] },
) {
  return (
    <Layout title="Overview">
      <Head title="Overview" />

      <Section title="Members" count={members.length}>
        <div className="space-y-3">
          {members.map((member) => <MemberCard key={member.id} member={member} plans={plans} />)}
        </div>
      </Section>

      <Section title="Coming up" count={upcoming.length}>
        <RequestList requests={upcoming} empty="Nothing requested for today or later." />
      </Section>

      <Section title="Last 7 days" count={recent.length}>
        <RequestList requests={recent} empty="Nothing in the last week." />
      </Section>
    </Layout>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 flex items-baseline gap-2 text-lg font-semibold tracking-tight">
        {title}
        <span className="text-sm font-normal text-neutral-500">{count}</span>
      </h2>
      {children}
    </section>
  );
}

function MemberCard({ member, plans }: { member: Member; plans: Plan[] }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-medium">
          {member.name ?? member.phone}
          {member.operator && <Tag tone="brand">operator</Tag>}
          {member.status !== "active" && <Tag tone="warn">{member.status}</Tag>}
        </div>
        <ConnectionBadge connection={member.connection} />
      </div>

      <div className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        {[member.club, member.email].filter(Boolean).join(" · ")}
      </div>

      {/* Tier is the one thing that legitimately changes for an existing member
          — a tester graduating to a real allowance — so it is editable here
          rather than in a console. Changing it patches immediately. */}
      <div className="mt-2 flex items-center gap-2 text-sm">
        <span className="text-neutral-500 dark:text-neutral-400">Tier:</span>
        <select
          value={member.planId ?? ""}
          onChange={(event) => {
            const planId = event.target.value;
            const plan = plans.find((p) => String(p.id) === planId);
            if (!plan || planId === String(member.planId)) return;
            if (confirm(`Move ${member.name ?? member.phone} to ${plan.name} (${plan.describe})?`)) {
              router.patch(`/overview/members/${member.id}`, { plan_id: planId }, { preserveScroll: true });
            }
          }}
          className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-800"
        >
          {member.planId === null && <option value="">— none —</option>}
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>{plan.name} ({plan.describe})</option>
          ))}
        </select>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>{member.activeRequests} active request{member.activeRequests === 1 ? "" : "s"}</span>
        <span>{member.upcomingCourts} upcoming court{member.upcomingCourts === 1 ? "" : "s"}</span>
      </div>

      {/* Who they may book for. Shown because getting this wrong put one
          member's request against another member's court, and nothing on any
          screen would have revealed it. */}
      <div className="mt-3 border-t border-neutral-100 pt-3 text-sm dark:border-neutral-800">
        <span className="text-neutral-500 dark:text-neutral-400">Can book for: </span>
        {member.contacts.filter((c) => c.bookable).length === 0
          ? <span className="text-neutral-400">nobody yet</span>
          : member.contacts.filter((c) => c.bookable).map((contact) => (
            <span key={contact.name} className="mr-2">
              {contact.name}{contact.self && <span className="text-neutral-400"> (self)</span>}
            </span>
          ))}

        {member.contacts.some((c) => !c.bookable) && (
          <div className="mt-1 text-xs text-neutral-400">
            Known but not bookable: {member.contacts.filter((c) => !c.bookable)
              .map((c) => c.name ?? "unnamed").join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionBadge({ connection }: { connection: Connection }) {
  if (connection.state === "expired") return <Tag tone="warn">reconnect needed</Tag>;
  if (connection.state === "never") return <Tag tone="warn">not connected</Tag>;

  return (
    <span className="text-xs text-neutral-500 dark:text-neutral-400">
      connected{connection.expires && ` · token to ${new Date(connection.expires)
        .toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
    </span>
  );
}

function RequestList({ requests, empty }: { requests: Request[]; empty: string }) {
  if (requests.length === 0) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">{empty}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500 dark:bg-neutral-900">
          <tr>
            <Th>When</Th><Th>Member</Th><Th>Where</Th><Th>Status</Th><Th>Result</Th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => (
            <tr key={request.id} className="border-t border-neutral-100 align-top dark:border-neutral-800">
              <Td>
                <div className="whitespace-nowrap font-medium">
                  {new Date(request.date).toLocaleDateString("en-GB",
                    { weekday: "short", day: "numeric", month: "short" })}
                </div>
                <div className="whitespace-nowrap text-neutral-500">
                  {request.startTime} · {request.duration}min
                </div>
                {request.backupTimes.length > 0 && (
                  <div className="whitespace-nowrap text-xs text-neutral-400">
                    or {request.backupTimes.join(", ")}
                  </div>
                )}
              </Td>
              <Td>
                {request.member}
                {request.repeating && <div className="text-xs text-neutral-400">weekly</div>}
              </Td>
              <Td>
                {request.club}
                <div className="text-neutral-500">{request.sport}</div>
              </Td>
              <Td>
                <StatusTag status={request.status} />
                {request.attempts > 0 && (
                  <div className="mt-1 text-xs text-neutral-400">{request.attempts} attempts</div>
                )}
              </Td>
              <Td>
                {request.courts.length > 0
                  ? request.courts.map((court, index) => (
                    <div key={index} className={court.status === "cancelled" ? "line-through opacity-60" : ""}>
                      {court.court ?? "court"} · {court.startTime} · {court.duration}min
                      {/* Whose membership it went on — the thing fix 2 was about. */}
                      {court.under && <span className="text-neutral-500"> — {court.under}</span>}
                    </div>
                  ))
                  : <span className="text-neutral-400">—</span>}

                {/* Otherwise this only exists in the log. */}
                {request.lastError && (
                  <div className="mt-1 text-xs text-neutral-500">{request.lastError}</div>
                )}

                {/* What David Lloyd actually offered. "No courts available" is
                    a different story depending on whether nothing was free or
                    everything free was ineligible, and only this says which. */}
                {request.attempts_seen.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-neutral-400">
                      what it saw
                    </summary>
                    <div className="mt-1 space-y-0.5">
                      {request.attempts_seen.map((attempt, index) => (
                        <div key={index} className="text-xs text-neutral-500">
                          {new Date(attempt.at).toLocaleTimeString("en-GB",
                            { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                          {" · "}{attempt.saw}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TONES: Record<string, string> = {
  fulfilled: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  partial: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  pending: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  processing: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  already_booked: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
  cancelled: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  blocked: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
};

function StatusTag({ status }: { status: string }) {
  const tone = TONES[status] ?? "bg-neutral-100 text-neutral-600";

  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function Tag({ tone, children }: { tone: "brand" | "warn"; children: React.ReactNode }) {
  const tones = {
    brand: "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300",
    warn: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  };

  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => <th className="px-3 py-2">{children}</th>;
const Td = ({ children }: { children: React.ReactNode }) => <td className="px-3 py-2">{children}</td>;
